/// <amd-module name="Artifacts/Feed/services/ArtifactsSettingsService" />

import * as Res from "Artifacts/Feed/Resources";
import { NormalizedGlobalPermission } from "Artifacts/Feed/types";
import { IFeed } from "Artifacts/Root/types";
import { GlobalPermission, GlobalRole } from "Artifacts/Root/web-apis/contracts/Feed";
import { getFeedClient, IFeedRestClient } from "Artifacts/Root/web-apis/RestClient/Feed";
import { IdentitiesSearchRequestModel, IIdentity } from "VSS/Features/Identities/IdentityService";
import { IIdentitiesRestClient } from "VSS/Features/Identities/RestClient/Identities";
import { IVssRestClientOptions, IVssService, Services, VssService } from "VSS/Platform/Context";
import { Identity } from "VSS/Platform/Contracts/Identities";

export interface IArtifactsSettingsData {
  everyoneIsCreator: boolean;
  creatorIds: IIdentity[];
  adminIds: IIdentity[];
  customNames: string[];
}

export interface IRoleAssignment {
  tfid: string;
  role: GlobalRole;
}

export interface IArtifactsSettingsService extends IVssService {
  saveArtifactsSettings(modifiedRoles: IRoleAssignment[]): Promise<void>;
  getArtifactsSettingsParams(feed: IFeed | undefined): Promise<IArtifactsSettingsData>;
}

export class ArtifactsSettingsService extends VssService implements IArtifactsSettingsService {
  private _feedRestClient: IFeedRestClient;
  private _tfidToDescriptor = new Map<string, string>();
  private _tfidToIIdentity = new Map<string, IIdentity>();

  public async saveArtifactsSettings(modifiedRoles: IRoleAssignment[]): Promise<void> {
    const modifiedRoleTfids = modifiedRoles.map((x) => x.tfid);
    await this.cacheIdentitiesByTfidAsync(modifiedRoleTfids);
    if (modifiedRoleTfids.some((x) => !this._tfidToDescriptor.has(x))) {
      throw new Error(Res.ArtifactsSettings_ReadIdentitiesError);
    }

    const globalPermissions = modifiedRoles.map((x) =>
      new NormalizedGlobalPermission(
        this._tfidToDescriptor.get(x.tfid)!,
        x.role,
        x.tfid,
      ).toGlobalPermission(),
    );

    await this._feedRestClient.setGlobalPermissions(globalPermissions);
  }

  public async getArtifactsSettingsParams(feed: IFeed): Promise<IArtifactsSettingsData> {
    this._feedRestClient = getFeedClient(this.pageContext);
    const permissions: GlobalPermission[] = await this._feedRestClient.getGlobalPermissions(true);

    const pcaIdentity = feed.accountMetadata.projectCollectionAdminGroupId;

    const normalizedPermissions = permissions
      .map(NormalizedGlobalPermission.fromGlobalPermission)
      .filter((x) => x.identityId !== pcaIdentity && x.role !== GlobalRole.None);

    const creators = normalizedPermissions.filter((x) => x.role === GlobalRole.FeedCreator);
    const admins = normalizedPermissions.filter((x) => x.role === GlobalRole.Administrator);
    const customs = normalizedPermissions.filter((x) => x.role === GlobalRole.Custom);

    const allIdentities = creators.concat(admins, customs);
    const identityMap: Map<string, IIdentity> = await this.getIIdentityFromPermissionAsync(
      allIdentities,
    );

    const everyoneGroupId = feed.accountMetadata.everyoneGroupId;
    const everyoneCreator = creators.find((x) => x.identityId === everyoneGroupId);
    const everyoneIsCreator = !!everyoneCreator;

    const creatorIds = creators.reduce((result: IIdentity[], creator) => {
      const identity = identityMap.get(creator.identityId);
      if (identity != null) {
        result.push(identity);
      }
      return result;
    }, []);

    const adminIds = admins.reduce((result: IIdentity[], admin) => {
      const identity = identityMap.get(admin.identityId);
      if (identity != null) {
        result.push(identity);
      }
      return result;
    }, []);

    const customNames = customs
      .map((x) => identityMap.get(x.identityId))
      .filter((x) => x != null)
      .map((x) => x!.displayName || x!.entityId);

    const initialValues: IArtifactsSettingsData = {
      everyoneIsCreator,
      creatorIds,
      adminIds,
      customNames,
    };

    return initialValues;
  }

  private async getIIdentityFromPermissionAsync(
    permissions: NormalizedGlobalPermission[],
  ): Promise<Map<string, IIdentity>> {
    const missing = permissions.filter(
      (permission) => !this._tfidToDescriptor.has(permission.identityId),
    );

    if (missing.length > 0) {
      const missingIdentities: string[] = [];
      for (const permission of missing) {
        this._tfidToDescriptor.set(permission.identityId, permission.identityDescriptor);
        missingIdentities.push(permission.identityId);
      }
      await this.cacheIIdentities(missingIdentities);
    }

    const idToIIdentity = new Map<string, IIdentity>();

    for (const permission of permissions) {
      const iidentity = this._tfidToIIdentity.get(permission.identityId);
      if (iidentity != null) {
        idToIIdentity.set(permission.identityId, iidentity);
      }
    }
    return idToIIdentity;
  }

  // takes an array of Identity ids and populates this._tfidToIIdentity
  private async cacheIIdentities(tfids: string[]): Promise<void> {
    const identityQuery = tfids.join(";");

    // IdentityService doesn't support multiple queries, so use IIdentityPickerHttpClient directly
    // but it's not currently exported :/ so 'any' it is...
    // import { IIdentityPickerHttpClient } from "VSS/Features/Identities/IdentityService";
    const identityPickerClient = this.pageContext.getRestClient<any>("IIdentityPickerHttpClient");

    const requestProps: IdentitiesSearchRequestModel = {
      query: identityQuery,
      identityTypes: ["user", "group"],
      operationScopes: ["ims"],
      queryTypeHint: "uid",
      options: {
        MinResults: 5,
        MaxResults: 40,
      },
      properties: [
        "DisplayName",
        "IsMru",
        "ScopeName",
        "SamAccountName",
        "Active",
        "SubjectDescriptor",
      ],
    };

    const identityResults = await identityPickerClient.beginGetIdentities(requestProps);
    if (identityResults != null && identityResults.results != null) {
      for (const result of identityResults.results) {
        for (const identity of result.identities) {
          if (identity == null || identity.localId == null) {
            continue;
          }

          this._tfidToIIdentity.set(identity.localId, identity);
        }
      }
    }
  }

  private async cacheIdentitiesByTfidAsync(tfids: string[]): Promise<void> {
    const missing = tfids.filter((x) => !this._tfidToDescriptor.has(x));

    if (missing.length === 0) {
      return;
    }

    const identityRestClient = this.pageContext.getRestClient<IIdentitiesRestClient>(
      "IIdentitiesRestClient",
      {
        serviceInstanceType: "951917ac-a960-4999-8464-e3f0aa25b381", // SPS
      } as IVssRestClientOptions,
    );

    return this.cacheIdentities(await identityRestClient.readIdentities(undefined, missing.join()));
  }

  private async cacheIdentities(identities: Identity[]): Promise<void> {
    const validIdentities: string[] = [];
    for (const identity of identities) {
      if (identity == null) {
        continue;
      }

      validIdentities.push(identity.id);

      const descriptor = NormalizedGlobalPermission.normalizeIdentityDescriptorToString(
        identity.descriptor,
      );
      this._tfidToDescriptor.set(identity.id, descriptor);
    }

    return this.cacheIIdentities(validIdentities);
  }
}

Services.add("IArtifactsSettingsService", { serviceFactory: ArtifactsSettingsService });
